<template>
  <v-app>
    <!-- App Bar / Header -->
    <v-app-bar :elevation="0" color="surface" height="64" app class="border-b">
      <v-app-bar-nav-icon @click="drawer = !drawer" class="d-lg-none" color="on-surface" />

      <!-- Rail toggle for larger screens -->
      <v-btn icon @click="rail = !rail" class="d-none d-lg-flex mr-2" color="on-surface" variant="text">
        <v-icon>{{ rail ? 'mdi-menu' : 'mdi-menu-open' }}</v-icon>
      </v-btn>

      <!-- Logo -->
      <nuxt-link to="/" class="logo-container ml-2">
        <img :src="logoSrc" :alt="applicationName + ' Logo'" class="logo" />
      </nuxt-link>
      <v-chip class="ml-3" color="error" variant="outlined"><strong>STAGING</strong></v-chip>
      <v-spacer />

      <!-- Notifications -->
      <v-btn icon variant="text" color="on-surface" class="mr-2">
        <v-badge content="3" color="error" offset-x="2" offset-y="2">
          <v-icon>mdi-bell-outline</v-icon>
        </v-badge>
      </v-btn>

      <!-- Theme Toggle -->
      <v-btn icon @click="toggleTheme" class="mr-2" variant="text" color="on-surface">
        <v-icon>{{ isDark ? 'mdi-brightness-7' : 'mdi-brightness-4' }}</v-icon>
      </v-btn>

      <!-- User Menu -->
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn variant="text" v-bind="props" class="mr-2">
            <v-avatar size="32" class="mr-2">
              <v-img src="https://cdn.vuetifyjs.com/images/john.jpg" alt="User Avatar" />
            </v-avatar>
            <div class="d-none d-sm-flex flex-column align-start">
              <span class="text-body-2 font-weight-medium">Tyler DeLange</span>
              <span class="text-caption text-medium-emphasis">Admin</span>
            </div>
            <v-icon class="ml-1">mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list width="200">
          <v-list-item prepend-icon="mdi-account-outline">
            <v-list-item-title>Profile</v-list-item-title>
          </v-list-item>
          <v-list-item prepend-icon="mdi-cog-outline">
            <v-list-item-title>Settings</v-list-item-title>
          </v-list-item>
          <v-list-item prepend-icon="mdi-help-circle-outline">
            <v-list-item-title>Help</v-list-item-title>
          </v-list-item>
          <v-divider />
          <v-list-item prepend-icon="mdi-logout" class="text-error">
            <v-list-item-title>Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Navigation Drawer / Sidebar -->
    <v-navigation-drawer v-model="drawer" :permanent="$vuetify.display.lgAndUp" app :width="280" :rail="rail" :expand-on-hover="rail" color="surface" class="sidebar" @mouseenter="isRailExpanded = true" @mouseleave="isRailExpanded = false">
      <!-- Logo Section -->

      <v-divider />

      <!-- Navigation Menu -->
      <v-list density="compact" nav class="pa-2" v-model:opened="expandedGroups">
        <template v-for="item in menuItems" :key="item.title">
          <v-list-group v-if="item.children" :value="item.title">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props" :prepend-icon="item.icon" :title="item.title" />
            </template>
            <v-list-item v-for="child in item.children" :key="child.title" :title="child.title" :to="child.to" :prepend-icon="child.icon" />
          </v-list-group>
          <v-list-item v-else :prepend-icon="item.icon" :title="item.title" :to="item.to" :value="item.title" />
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content Area -->
    <v-main class="bg-background">
      <v-container fluid class="pa-6">
        <slot />
      </v-container>

      <!-- Footer -->
      <div class="footer border-t">
        <v-container fluid class="pa-4">
          <v-row align="center" justify="space-between">
            <v-col cols="12" sm="6" class="text-left">
              <span class="text-body-2 text-medium-emphasis"> © 2025 Tessella Technologies. All rights reserved. </span>
            </v-col>
            <v-col cols="12" sm="6" class="text-right">
              <v-btn variant="text" size="small" color="primary" prepend-icon="mdi-help-circle-outline" href="#"> Support </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { useTheme } from 'vuetify'
import logoLight from '~/assets/images/logos/logo_snowflow_text_light.svg'
import logoDark from '~/assets/images/logos/logo_snowflow_text_dark.svg'

// Store Variables
const { applicationName } = storeToRefs(useGlobalsStore())
const theme = useTheme()
const drawer = ref(true)
const rail = ref(true) // Start in rail mode
const expandedGroups = ref<string[]>([])
const expandedGroupsState = ref<string[]>([])
const isRailExpanded = ref(false) // Track if rail is expanded due to hover
const isDark = computed(() => theme.global.name.value === 'dark')

// Computed property for logo source based on theme
const logoSrc = computed(() => {
  return isDark.value ? logoDark : logoLight
})

// //////////////////////////////////////////////////////////////////
// 💬 Menu SideItems Shouldn't Show When The Rail is Collapsed
// //////////////////////////////////////////////////////////////////
watch(isRailExpanded, (newVal) => {
  // If Rail is Not Expanded and Rail is not pinned
  if (!newVal && rail.value) {
    expandedGroupsState.value = expandedGroups.value
    expandedGroups.value = []
  } else {
    // Restore Expanded Groups when rail is expanded
    expandedGroups.value = expandedGroupsState.value
  }
})

onMounted(() => {
  // 💬 Keep Expanded Menu Items From Showing When Rail is Collapsed
  // ///////////////////////////////////////////////////////////////
  if (rail.value) {
    expandedGroupsState.value = expandedGroups.value
    expandedGroups.value = []
  }
})

const toggleTheme = () => {
  theme.global.name.value = theme.global.current.value.dark ? 'light' : 'dark'
}

// Import menu items from composable
const { menuItems } = useMenuItems()
</script>

<style scoped>
.sidebar {
  border-right: 1px solid rgb(var(--v-theme-surface-variant)) !important;
}

.border-b {
  border-bottom: 1px solid rgb(var(--v-theme-surface-variant)) !important;
}

.border-t {
  border-top: 1px solid rgb(var(--v-theme-surface-variant)) !important;
}

.search-field {
  border-radius: 8px !important;
}

.nav-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
}

.v-list-item--active {
  background: linear-gradient(98deg, rgba(var(--v-theme-primary), 0.16) 0%, rgba(var(--v-theme-primary), 0.08) 100%) !important;
  color: rgb(var(--v-theme-primary)) !important;
}

.v-list-item--active .v-icon {
  color: rgb(var(--v-theme-primary)) !important;
}

.logo-container {
  display: flex;
  align-items: center;
  height: 100%;
}

.logo {
  max-height: 72px;
  width: auto;
  object-fit: contain;
  transition: opacity 0.2s ease;
}

.logo:hover {
  opacity: 0.8;
}

.footer {
  background-color: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface));
  min-height: 60px;
}

.footer .v-container {
  max-width: 100%;
}

/* Responsive footer adjustments */
@media (max-width: 600px) {
  .footer .text-right {
    text-align: left !important;
  }

  .footer .v-col {
    padding-top: 8px;
    padding-bottom: 8px;
  }
}
</style>
